type TreeSeat = { id: string; parent_id?: string | null };

export type RosterCollapse = {
  /** Seats drawn after collapsed branches are removed, in input order. */
  shown: TreeSeat['id'][];
  /** Number of drawn-before-collapse children per seat. */
  childCount: Map<string, number>;
  /** Descendants hidden under each collapsed seat that is itself drawn. */
  hiddenCount: Map<string, number>;
  /** Seats with children that are not at the top of the chart. */
  collapsible: string[];
};

/**
 * Removes the descendants of collapsed seats. Only seats in `seats` count, so a
 * branch the filter already hid neither earns a chevron nor adds to "+N".
 */
export function collapseRosterTree(
  seats: readonly TreeSeat[],
  collapsed: ReadonlySet<string>
): RosterCollapse {
  const ids = new Set(seats.map((seat) => seat.id));
  const children = new Map<string, string[]>();
  for (const seat of seats) {
    if (!seat.parent_id || !ids.has(seat.parent_id)) continue;
    children.set(seat.parent_id, [
      ...(children.get(seat.parent_id) ?? []),
      seat.id,
    ]);
  }

  const countDescendants = (id: string): number => {
    let total = 0;
    const stack = [...(children.get(id) ?? [])];
    while (stack.length > 0) {
      const next = stack.pop()!;
      total += 1;
      stack.push(...(children.get(next) ?? []));
    }
    return total;
  };

  const hidden = new Set<string>();
  const hiddenCount = new Map<string, number>();
  const roots = seats.filter(
    (seat) => !seat.parent_id || !ids.has(seat.parent_id)
  );
  const stack = roots.map((seat) => seat.id);
  while (stack.length > 0) {
    const id = stack.pop()!;
    const kids = children.get(id) ?? [];
    if (collapsed.has(id) && kids.length > 0) {
      hiddenCount.set(id, countDescendants(id));
      const drop = [...kids];
      while (drop.length > 0) {
        const next = drop.pop()!;
        hidden.add(next);
        drop.push(...(children.get(next) ?? []));
      }
      continue;
    }
    stack.push(...kids);
  }

  const rootIds = new Set(roots.map((seat) => seat.id));
  return {
    shown: seats.filter((seat) => !hidden.has(seat.id)).map((seat) => seat.id),
    childCount: new Map(
      [...children].map(([id, kids]) => [id, kids.length] as const)
    ),
    hiddenCount,
    collapsible: [...children.keys()].filter((id) => !rootIds.has(id)),
  };
}

const storageKey = (incidentId: string) =>
  `roster-chart-collapsed:${incidentId || 'draft'}`;

export function readCollapsedSeats(incidentId: string): Set<string> {
  try {
    const raw = sessionStorage.getItem(storageKey(incidentId));
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return new Set(
      Array.isArray(parsed)
        ? parsed.filter((id): id is string => typeof id === 'string')
        : []
    );
  } catch {
    return new Set();
  }
}

export function persistCollapsedSeats(
  incidentId: string,
  collapsed: ReadonlySet<string>
) {
  try {
    sessionStorage.setItem(
      storageKey(incidentId),
      JSON.stringify([...collapsed])
    );
  } catch {
    // Ignore private-mode / quota failures.
  }
}
